import { Search } from 'lucide-react';
import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AddFilterMenu } from './AddFilterMenu';
import { FilterChip } from './FilterChip';
import { optionLabel, type FilterField, type FilterOption } from './filters';
import { MonthPicker } from './MonthPicker';
import { PageSizeSelect } from './PageSizeSelect';
import type { PageSize } from './use-list-params';
import { useSearchField } from './use-search-field';
import { ViewTabs, type ListView } from './ViewTabs';

function ToolbarSearch({
  label,
  typed,
  onType,
}: {
  readonly label: string;
  readonly typed: string;
  readonly onType: (value: string) => void;
}) {
  const searchId = useId();
  return (
    <div className="list-toolbar-search">
      <label htmlFor={searchId} className="sr-only">
        {label}
      </label>
      <Search
        aria-hidden="true"
        className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
      />
      <Input
        id={searchId}
        type="search"
        value={typed}
        onChange={(event) => onType(event.target.value)}
        placeholder={label}
        className="pl-9"
      />
    </div>
  );
}

function SortSelect({
  sort,
}: {
  readonly sort: {
    readonly label: string;
    readonly value: string;
    readonly options: readonly FilterOption[];
    readonly onChange: (value: string) => void;
  };
}) {
  return (
    <Select value={sort.value} onValueChange={sort.onChange}>
      <SelectTrigger aria-label={sort.label} className="list-toolbar-chip">
        <span className="text-muted-foreground">{sort.label}:</span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {sort.options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * The shared filterable list's toolbar (decision 101, #401, the boards approved in decision 105).
 *
 * - The search on the left, the order on the right.
 * - Under them, each filter in force as a label that removes itself, «+ Filtro» to add one, and
 *   «Azzera i filtri» whenever anything narrows the list.
 *
 * It holds no state of its own beyond the typed search: the caller keeps search, filters and order
 * in the URL (`useListParams`) and passes them in.
 */
export function ListToolbar({
  label,
  search,
  fields,
  active,
  onFilter,
  onClear,
  sort,
  views,
  pageSize,
  viewsAside,
}: {
  /** Names the toolbar for assistive technology, usually the list's own title. */
  readonly label: string;
  readonly search: {
    readonly label: string;
    readonly value: string;
    readonly onWrite: (q: string) => void;
  };
  readonly fields: readonly FilterField[];
  readonly active: Readonly<Record<string, string | null>>;
  readonly onFilter: (key: string, value: string | null) => void;
  readonly onClear: () => void;
  readonly sort?: {
    readonly label: string;
    readonly value: string;
    readonly options: readonly FilterOption[];
    readonly onChange: (value: string) => void;
  };
  /** Optional bar of named views above the search; absent, nothing changes. */
  readonly views?: {
    readonly label: string;
    readonly items: readonly ListView[];
    readonly current: { get: (key: string) => string | null };
    readonly onSelect: (view: ListView) => void;
  };
  /** Sits with the views, on their row (a switch that changes what the views count). */
  readonly viewsAside?: ReactNode;
  /** Optional page-size choice next to the order; absent, nothing changes. */
  readonly pageSize?: {
    readonly value: PageSize;
    readonly onChange: (size: PageSize) => void;
  };
}) {
  const { t } = useTranslation('common');
  const [typed, setTyped] = useSearchField(search.value, search.onWrite);
  const regularFields = fields.filter((field) => !field.monthPicker);
  const inForce = regularFields.filter((field) => active[field.key]);
  const narrowed = search.value !== '' || fields.some((field) => active[field.key]);

  return (
    <div
      role="toolbar"
      aria-label={label}
      className="list-toolbar list-toolbar-stacked max-w-full min-w-0"
    >
      {views ? (
        <div className="list-toolbar-row max-w-full min-w-0">
          <ViewTabs
            label={views.label}
            views={views.items}
            current={views.current}
            onSelect={views.onSelect}
          />
          {viewsAside}
        </div>
      ) : null}
      <div className="list-toolbar-row">
        <ToolbarSearch label={search.label} typed={typed} onType={setTyped} />
        <span className="list-toolbar-spacer" />
        {sort ? <SortSelect sort={sort} /> : null}
        {pageSize ? <PageSizeSelect value={pageSize.value} onChange={pageSize.onChange} /> : null}
      </div>
      {fields.length > 0 || narrowed ? (
        <div className="list-toolbar-row list-toolbar-filters">
          {fields
            .filter((field) => field.monthPicker)
            .map((field) => (
              <MonthPicker
                key={field.key}
                label={field.label}
                value={active[field.key] ?? null}
                onChange={(value) => onFilter(field.key, value)}
              />
            ))}
          {inForce.map((field) => (
            <FilterChip
              key={field.key}
              label={field.label}
              value={optionLabel(field, active[field.key] ?? '')}
              onRemove={() => onFilter(field.key, null)}
            />
          ))}
          {regularFields.length > 0 ? (
            <AddFilterMenu fields={regularFields} active={active} onChoose={onFilter} />
          ) : null}
          {/* Whenever something narrows the list, not only when nothing is left. */}
          {narrowed ? (
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11 px-2 text-[13.5px]"
              onClick={onClear}
            >
              {t('list.clear')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
